import { type QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { queryKeysUser } from '~/data/user/queryKeys';
import { fetchMyUser } from '~/data/user/useQueryMyUser/fetchMyUser';
import {
  type MyUser,
  type PermissionLevel,
} from '~/data/user/useQueryMyUser/types';
import { INTERNAL_ADMIN_PERMISSION_LEVELS } from '~/data/user/useQueryMyUser/validations';

import { RouterContext } from './types';

const externalAdminFallbackRoute = '/ai-parser-review';

type RouteProtectionConfig = {
  /**
   * Required permission level for accessing this route.
   * User must have at least one matching permission level.
   * @example
   * ['admin', 'external_admin']
   */
  permissionLevel?: PermissionLevel[];
};

/**
 * Fetches user data independently of TanStack Query's observer lifecycle
 * to avoid CancelledError when React Strict Mode destroys useQuery observers.
 */
async function getUserData(queryClient: QueryClient) {
  const cached = queryClient.getQueryData<MyUser>(queryKeysUser.getMe());
  if (cached) return cached;

  try {
    const userData = await fetchMyUser();
    if (userData) {
      queryClient.setQueryData(queryKeysUser.getMe(), userData);
    }
    return userData;
  } catch {
    return null;
  }
}

/**
 * Creates a beforeLoad hook that checks if the user is authorized to access a route
 * based on the provided permissions.
 *
 * @param config - Route protection configuration
 * @returns A beforeLoad function that redirects or aborts if the user lacks permissions
 */
export function createProtectedRoute(config: RouteProtectionConfig) {
  const { permissionLevel } = config;

  return async ({ context }: { context: RouterContext }) => {
    const { auth, queryClient } = context;

    const userId = auth.getUserIdFromAccessToken();
    const isAuthenticated = Boolean(userId);

    // If not authenticated, let AppInitializer handle OIDC login flow
    // Don't redirect to /unauthorized - that's only for authorization failures
    if (!isAuthenticated) {
      return {
        user: null,
      };
    }

    const userData = await getUserData(queryClient);

    // This should not happen if authenticated, but handle gracefully
    if (!userData) {
      throw redirect({ to: '/unauthorized', throw: true });
    }

    // Check permissions - redirect external admins to their accessible landing route.
    const hasPermission = checkUserPermissions(userData, permissionLevel);
    if (!hasPermission) {
      if (userData.permissionLevel === 'external_admin') {
        redirect({
          to: externalAdminFallbackRoute,
          throw: true,
        });
      }

      redirect({
        to: '/unauthorized',
        throw: true,
      });
    }

    return {
      user: userData,
    };
  };
}

/**
 * Checks if user has the required permissions.
 */
export function checkUserPermissions(
  userData: MyUser,
  requiredPermissionLevel?: PermissionLevel[],
) {
  if (!requiredPermissionLevel?.length) {
    return true;
  }

  if (!userData?.permissionLevel) {
    return false;
  }

  const userPermissionLevel = userData.permissionLevel;

  return (
    requiredPermissionLevel.includes(userPermissionLevel) ||
    (requiredPermissionLevel.includes('admin') &&
      INTERNAL_ADMIN_PERMISSION_LEVELS.some(
        (level) => level === userPermissionLevel,
      ))
  );
}
